/** A small typed event emitter: `on` returns the function that unsubscribes. */
export type Listener<T> = (value: T) => void;

export interface Emitter<Events extends object> {
  on<K extends keyof Events>(name: K, listener: Listener<Events[K]>): () => void;
  emit<K extends keyof Events>(name: K, value: Events[K]): void;
}

export function createEmitter<Events extends object>(): Emitter<Events> {
  const listeners = new Map<keyof Events, Set<Listener<never>>>();
  return {
    on(name, listener) {
      let set = listeners.get(name);
      if (!set) {
        set = new Set();
        listeners.set(name, set);
      }
      set.add(listener);
      return () => {
        set.delete(listener);
      };
    },
    emit(name, value) {
      const set = listeners.get(name);
      if (!set) return;
      for (const listener of [...set]) (listener as Listener<typeof value>)(value);
    },
  };
}
